/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { createFeatureSelector, createSelector } from '@ngrx/store';

import { TenantSettingsKey } from '@core/api/tenant/tenant.enums';
import { ThemePreference } from '@core/api/user-preferences/user-preferences.models';

import { PermissionStoreModelEnum } from '@domain/permissions/permissions.enums';
import { doesSettingsHaveKey, mapTenantSettingsToFeatureFlags } from '@domain/tenant/tenant-models.mappings';
import { FeatureFlags } from '@domain/tenant/tenant.enums';

import { isDefined } from '@shared/utils/is-defined';

import { AuthState, authFeatureKey } from './auth.reducer';
import { mapThemeColorsStoreModelToThemeColorVariableValues } from './mappings';

export const selectAuthFeature = createFeatureSelector<AuthState>(authFeatureKey);
export const selectOriginalAccessToken = createSelector(selectAuthFeature, (state) => state.accessToken);
export const selectImpersonationToken = createSelector(selectAuthFeature, (state) => state.impersonationToken);
export const selectImpersonationTargetUserId = createSelector(selectAuthFeature, (state) => state.impersonationTargetUserId);
export const selectImpersonationIsLoading = createSelector(selectAuthFeature, (state) => state.impersonationIsLoading ?? false);
export const selectInitialImpersonationIsLoading = createSelector(
  selectAuthFeature,
  (state) => state.initialImpersonationIsLoading ?? false
);
export const selectImpersonationError = createSelector(selectAuthFeature, (state) => state.impersonationError);
export const selectAccessToken = createSelector(
  selectOriginalAccessToken,
  selectImpersonationToken,
  (token, impersonationToken) => impersonationToken ?? token
);
export const selectRefreshTokenExpiresTimestamp = createSelector(selectAuthFeature, (state) => state.refreshTokenExpiresTimestamp);
export const selectPermissions = createSelector(selectAuthFeature, (state) => state.impersonationPermissions ?? state.permissions);
export const selectLightTheme = createSelector(selectAuthFeature, (state) => state.lightTheme);
export const selectDarkTheme = createSelector(selectAuthFeature, (state) => state.darkTheme);
export const selectThemePreference = createSelector(selectAuthFeature, (state) => state.themePreference);
export const selectSystemPrefersDark = createSelector(selectAuthFeature, (state) => state.systemPrefersDark);
export const selectIsSavingUserPreferences = createSelector(selectAuthFeature, (state) => state.isSavingUserPreferences);

export const selectIsDarkThemeAvailable = createSelector(selectLightTheme, selectDarkTheme, (lightTheme, darkTheme) => {
  if (!lightTheme || !darkTheme) {
    return;
  }

  return JSON.stringify(lightTheme) !== JSON.stringify(darkTheme);
});

export const selectTheme = createSelector(
  selectLightTheme,
  selectDarkTheme,
  selectThemePreference,
  selectSystemPrefersDark,
  selectIsDarkThemeAvailable,
  (lightTheme, darkTheme, themePreference, systemPrefersDark, isDarkThemeAvailable) => {
    if (!lightTheme || !darkTheme || !themePreference || systemPrefersDark === undefined) {
      return;
    }

    if (!isDarkThemeAvailable) {
      return lightTheme;
    }

    const effectiveDark = themePreference === ThemePreference.Dark || (themePreference !== ThemePreference.Light && !!systemPrefersDark);
    return effectiveDark ? darkTheme : lightTheme;
  }
);

export const selectThemeColorVariableValues = createSelector(selectTheme, (theme) =>
  theme ? mapThemeColorsStoreModelToThemeColorVariableValues(theme.colors) : undefined
);

export const selectIsDarkTheme = createSelector(
  selectThemePreference,
  selectSystemPrefersDark,
  selectIsDarkThemeAvailable,
  (themePreference, systemPrefersDark, isDarkThemeAvailable) => {
    if (!themePreference || systemPrefersDark === undefined || isDarkThemeAvailable === undefined) {
      return;
    }

    if (!isDarkThemeAvailable) {
      return false;
    }

    return themePreference === ThemePreference.Dark || (themePreference !== ThemePreference.Light && !!systemPrefersDark);
  }
);

export const selectHeaderMode = createSelector(selectTheme, (theme) => theme?.headerMode);
export const selectExpandedLogo = createSelector(selectTheme, (theme) => theme?.expandedLogo);
export const selectCollapsedLogo = createSelector(selectTheme, (theme) => theme?.collapsedLogo);
export const selectName = createSelector(selectAuthFeature, (state) => state.impersonationName ?? state.name);
export const selectOriginalName = createSelector(selectAuthFeature, (state) => state.name);
export const selectOrganisationId = createSelector(selectAuthFeature, (state) => state.impersonationOrganisationId ?? state.organisationId);
export const selectIsOptimoTenant = createSelector(selectOrganisationId, (organisationId) => organisationId?.toLowerCase() === 'optimo');

export const selectTenantSettings = createSelector(selectAuthFeature, (state) => state.tenantSettings);
export const selectTenantSettingsIsLoading = createSelector(selectAuthFeature, (state) => state.tenantSettingsIsLoading);
export const selectTenantSettingsError = createSelector(selectAuthFeature, (state) => state.tenantSettingsError);

export const selectFeatureFlags = createSelector(selectTenantSettings, (settings) => {
  if (!settings) {
    return;
  }

  return mapTenantSettingsToFeatureFlags(settings);
});

export const selectIsDashboardEnabled = createSelector(selectAuthFeature, (state) => {
  if (!isDefined(state.tenantSettings) && !isDefined(state.tenantSettingsError)) {
    return;
  }

  return doesSettingsHaveKey(state.tenantSettings?.settings ?? [], TenantSettingsKey.WebappDashboardEnabled);
});

export const selectIsGlobalVehiclesEnabled = createSelector(selectAuthFeature, (state) => {
  if (!isDefined(state.tenantSettings) && !isDefined(state.tenantSettingsError)) {
    return;
  }

  return doesSettingsHaveKey(state.tenantSettings?.settings ?? [], TenantSettingsKey.WebappGlobalVehiclesEnabled);
});

export const selectIsGlobalChargersEnabled = createSelector(selectAuthFeature, (state) => {
  if (!isDefined(state.tenantSettings) && !isDefined(state.tenantSettingsError)) {
    return;
  }

  return doesSettingsHaveKey(state.tenantSettings?.settings ?? [], TenantSettingsKey.WebappGlobalChargersEnabled);
});

export const selectIsVehicleStateOfChargeSummaryEnabled = createSelector(selectAuthFeature, (state) => {
  if (!isDefined(state.tenantSettings) && !isDefined(state.tenantSettingsError)) {
    return;
  }

  return doesSettingsHaveKey(state.tenantSettings?.settings ?? [], TenantSettingsKey.WebappDashboardVehicleStateOfChargeSummaryEnabled);
});

export const selectUserHasPermission = (permission: PermissionStoreModelEnum) =>
  createSelector(selectPermissions, (userPermissions) => !!userPermissions?.includes(permission));
export const selectUserHasEitherPermissions = (permissions: PermissionStoreModelEnum[]) =>
  createSelector(selectPermissions, (userPermissions) => !!userPermissions?.some((permission) => permissions.includes(permission)));

export const selectTenantHasFeatureFlag = (flag: FeatureFlags) =>
  createSelector(selectFeatureFlags, (featureFlags) => !!featureFlags?.includes(flag));
export const selectTenantHasEitherFeatureFlags = (flags: FeatureFlags[]) =>
  createSelector(selectFeatureFlags, (featureFlags) => !!featureFlags?.some((flag) => flags.includes(flag)));
